
import { ImageUploader } from "@/utils/image/ImageUploader";
import { dataUrlToFile } from "@/utils/image/convertUtils";
import { toast } from "@/hooks/use-toast";
import { getAuthHeader } from "@/services/authService";

/**
 * Image data structure from parser
 */
export interface ParsedImage {
  url?: string;
  alt?: string;
  title?: string;
}

/**
 * Parses the text input containing image information
 * Format:
 * url: https://example.com/image.jpg
 * alt text: Description of the image
 * title: Title of the image
 */
export const parseImageText = (text: string): ParsedImage[] => {
  const images: ParsedImage[] = [];
  const lines = text.split('\n');
  
  let currentImage: ParsedImage = {};
  
  for (const line of lines) {
    const trimmedLine = line.trim();
    
    if (trimmedLine === '') continue;
    
    if (trimmedLine.startsWith('url:')) {
      if (currentImage.url) {
        images.push({...currentImage});
        currentImage = {};
      }
      currentImage.url = trimmedLine.substring(4).trim();
    } else if (trimmedLine.startsWith('alt text:')) {
      currentImage.alt = trimmedLine.substring(9).trim();
    } else if (trimmedLine.startsWith('title:')) {
      currentImage.title = trimmedLine.substring(6).trim();
    }
  }
  
  if (currentImage.url) {
    images.push(currentImage);
  }
  
  return images;
};

/**
 * Checks if a URL is from Bubble.io
 */
export const isBubbleUrl = (url: string): boolean => {
  return url.includes('bubble.io');
};

/**
 * Process an image via the image proxy function.
 */
export const processImageViaProxy = async (imageUrl: string): Promise<File> => {
  console.log(`Processing image via proxy: ${imageUrl}`);
  
  try {
    const response = await fetch('/api/image-proxy', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ url: imageUrl, base64: true }),
    });

    if (!response.ok) {
      throw new Error(`Edge function error: ${response.statusText}`);
    }

    const data = await response.json();

    if (!data || !data.dataUrl) {
      throw new Error('No data URL returned from proxy');
    }
    
    // Convert the data URL to a file
    const extension = data.contentType.split('/')[1] || 'webp';
    const filename = `image_${Date.now()}.${extension}`;
    const file = dataUrlToFile(data.dataUrl, filename);
    
    console.log(`Successfully processed image via proxy: ${file.size} bytes`);
    return file;
  } catch (error: any) {
    console.error("Error in processImageViaProxy:", error);
    throw error;
  }
};

/**
 * Processes a single image and uploads it to storage.
 */
export const processAndUploadImage = async (
  image: ParsedImage, 
  blogTitle: string
): Promise<void> => {
  if (!image.url) return;
  
  let file: File;
  const imageUrl = image.url;
  
  // Handle image acquisition differently based on source
  if (isBubbleUrl(imageUrl)) {
    // Use our enhanced proxy for Bubble.io URLs
    file = await processImageViaProxy(imageUrl);
  } else {
    // For non-Bubble URLs, fetch directly
    const response = await fetch(imageUrl);
    if (!response.ok) {
      throw new Error(`Failed to fetch image: ${response.statusText}`);
    }
    const blob = await response.blob();
    const fileName = imageUrl.split('/').pop() || `image_${Date.now()}`;
    file = new File([blob], fileName, { type: blob.type });
  }
  
  const uploadedUrl = await ImageUploader.uploadToStorage(file, "blog_images/inline");
  
  const response = await fetch('/api/admin/blog-images', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...getAuthHeader(),
    },
    body: JSON.stringify({
      blog_title: blogTitle.trim(),
      image_url: uploadedUrl,
      original_url: image.url,
      image_alt: image.alt || '',
      image_title: image.title || '',
    }),
  });

  if (!response.ok) {
    const errorBody = await response.json().catch(() => ({}));
    throw new Error(`Database error: ${errorBody.error || response.statusText}`);
  }
};
